import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { SENIORITY } from "@/lib/jobFields";

export const TIMELINES = ["urgent", "1_3_months", "flexible"];
export const ENGAGEMENTS = ["engaged", "contingent", "fractional", "advisory"];

export const emptyOrg = {
  name: "", type: "startup", website: "", industry_tags: "",
  primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
  roles: [{ title: "", seniority: "" }],
  timeline: "flexible", engagement_type: "engaged", budget: "", notes: "",
};

export const orgToForm = (o) => ({
  name: o.name || "", type: o.type || "startup", website: o.website || "",
  industry_tags: (o.industry_tags || []).join(", "),
  primary_contact_name: o.primary_contact_name || "", primary_contact_email: o.primary_contact_email || "",
  primary_contact_phone: o.primary_contact_phone || "",
  roles: (o.roles && o.roles.length ? o.roles.map((r) => ({ title: r.title || "", seniority: r.seniority || "" })) : [{ title: "", seniority: "" }]),
  timeline: o.timeline || "flexible", engagement_type: o.engagement_type || "engaged",
  budget: o.budget || "", notes: o.notes || "",
});

export const formToOrgPayload = (form) => ({
  name: form.name, type: form.type, website: form.website,
  industry_tags: (form.industry_tags || "").split(",").map((t) => t.trim()).filter(Boolean),
  primary_contact_name: form.primary_contact_name, primary_contact_email: form.primary_contact_email,
  primary_contact_phone: form.primary_contact_phone,
  roles: (form.roles || []).map((r) => ({ title: (r.title || "").trim(), seniority: r.seniority || "" })).filter((r) => r.title),
  timeline: form.timeline, engagement_type: form.engagement_type, budget: form.budget, notes: form.notes,
});

export function OrgFormFields({ form, setForm, meta }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "bg-white border-black/10 rounded-none";
  const setRole = (i, key, v) => setForm({ ...form, roles: form.roles.map((r, j) => (j === i ? { ...r, [key]: v } : r)) });
  const addRole = () => setForm({ ...form, roles: [...form.roles, { title: "", seniority: "" }] });
  const removeRole = (i) => setForm({ ...form, roles: form.roles.filter((_, j) => j !== i) });

  return (
    <>
      <p className="overline text-[0.55rem]">Company</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Name</Label><Input data-testid="org-name" value={form.name} onChange={upd("name")} required className={input} /></div>
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="org-type" className={input}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">
              {meta.org_types.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Website</Label><Input value={form.website} onChange={upd("website")} className={input} placeholder="https://" /></div>
        <div className="space-y-2"><Label>Industry tags (comma sep)</Label><Input value={form.industry_tags} onChange={upd("industry_tags")} className={input} placeholder="fintech, ai, saas" /></div>
      </div>

      <p className="overline text-[0.55rem] pt-2">Primary contact</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2"><Label>Name</Label><Input data-testid="org-contact-name" value={form.primary_contact_name} onChange={upd("primary_contact_name")} className={input} /></div>
        <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="org-contact-email" value={form.primary_contact_email} onChange={upd("primary_contact_email")} className={input} /></div>
        <div className="space-y-2"><Label>Phone</Label><Input data-testid="org-contact-phone" value={form.primary_contact_phone} onChange={upd("primary_contact_phone")} className={input} /></div>
      </div>

      <p className="overline text-[0.55rem] pt-2">Search need</p>
      <div className="space-y-2">
        <Label>Role(s) hiring for <span className="text-muted-foreground font-normal">— set seniority per role</span></Label>
        <div className="space-y-2">
          {form.roles.map((r, i) => (
            <div key={i} className="flex gap-2">
              <Input data-testid={`org-role-${i}`} value={r.title} onChange={(e) => setRole(i, "title", e.target.value)} placeholder="e.g. VP Engineering" className={`${input} flex-1`} />
              <Select value={r.seniority} onValueChange={(v) => setRole(i, "seniority", v)}>
                <SelectTrigger data-testid={`org-role-seniority-${i}`} className={`${input} w-[46%]`}><SelectValue placeholder="Seniority" /></SelectTrigger>
                <SelectContent className="bg-white border-black/10 max-h-64">
                  {SENIORITY.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.roles.length > 1 && (
                <button type="button" data-testid={`org-remove-role-${i}`} onClick={() => removeRole(i)} className="shrink-0 w-11 flex items-center justify-center bg-[#F4F4F5] border border-black/10 hover:bg-black/5 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button type="button" data-testid="org-add-role" onClick={addRole} className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add another role
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>Timeline</Label>
          <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
            <SelectTrigger data-testid="org-timeline" className={input}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">
              {TIMELINES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Engagement</Label>
          <Select value={form.engagement_type} onValueChange={(v) => setForm({ ...form, engagement_type: v })}>
            <SelectTrigger data-testid="org-engagement" className={input}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">
              {ENGAGEMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Budget</Label><Input data-testid="org-budget" value={form.budget} onChange={upd("budget")} placeholder="$50k fee" className={input} /></div>
      </div>
      <div className="space-y-2"><Label>Notes</Label><Textarea data-testid="org-notes" value={form.notes} onChange={upd("notes")} className={`${input} min-h-[80px]`} placeholder="Context, must-haves, company stage…" /></div>
    </>
  );
}
